import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider } from "./context/AuthContext";
import ProtectedRoute from "./components/ProtectedRoute";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import AIChatbot from "./components/AIChatbot";
import Home from "./pages/Home";
import Courses from "./pages/Courses";
import CourseDetail from "./pages/CourseDetail";
import Checkout from "./pages/Checkout";
import StripeSuccess from "./pages/StripeSuccess";
import OrderConfirmation from "./pages/OrderConfirmation";
import BankTransferConfirmation from "./pages/BankTransferConfirmation";
import ProofOfPaymentForm from "./pages/ProofOfPaymentForm";
import OrderPortal from "./pages/OrderPortal";
import FeedbackPage from "./pages/FeedbackPage";
import AdminDashboard from "./pages/AdminDashboard";
import AdminLogin from "./pages/AdminLogin";
import VerifyProforma from "./pages/VerifyProforma";
import AttendeesForm from "./pages/AttendeesForm";
import UsLanding from "./pages/UsLanding";

const LEGACY_STATE_PAGES = ["mississippi", "alabama", "louisiana", "tennessee", "texas"];
import About from "./pages/About";
import Contact from "./pages/Contact";
import FAQ from "./pages/FAQ";
import CorporateTraining from "./pages/CorporateTraining";
import Consulting from "./pages/Consulting";
import InHouseTraining from "./pages/InHouseTraining";
import Consultants from "./pages/Consultants";
import Privacy from "./pages/Privacy";
import Terms from "./pages/Terms";
import Tools from "./pages/Tools";
import BlogDetail from "./pages/BlogDetail";
import ServiceDetail from "./pages/ServiceDetail";
import "./App.css";

// Subdomain detection hook
const useSubdomain = () => {
  const [isTrainingSubdomain, setIsTrainingSubdomain] = useState(false);
  
  useEffect(() => {
    const hostname = window.location.hostname;
    // Check if accessed via training.comply-cms.com or training subdomain
    if (hostname.startsWith('training.') || hostname.includes('training.comply-cms')) {
      setIsTrainingSubdomain(true);
    }
  }, []);
  
  return isTrainingSubdomain;
};

// Wrapper component to handle subdomain redirect
const SubdomainRedirect = ({ children }) => {
  const isTrainingSubdomain = useSubdomain();
  const location = useLocation();
  
  // If on training subdomain and at root, redirect to /courses
  if (isTrainingSubdomain && location.pathname === '/') {
    return <Navigate to="/courses" replace />;
  }
  
  return children;
};

function App() {
  return (
    <div className="App min-h-screen flex flex-col">
      <BrowserRouter>
        <AuthProvider>
          <SubdomainRedirect>
            <ChromeShell />
          </SubdomainRedirect>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

// Hide public Navbar / Footer / AIChatbot on admin routes so the admin has a clean SaaS shell.
function ChromeShell() {
  const location = useLocation();
  const isAdmin = location.pathname.startsWith("/admin") && location.pathname !== "/admin/login";
  const isFeedback = location.pathname.startsWith("/feedback");
  const isStandalone = isAdmin || isFeedback;
  useEffect(() => {
    const t = setTimeout(() => {
      if (!document.title) document.title = "Compliance Management Systems | PECB Training & ISO Consulting";
    }, 50);
    return () => clearTimeout(t);
  }, [location.pathname]);
  return (
    <>
      {!isStandalone && <Navbar />}
      <main className={isStandalone ? "flex-1" : "flex-1"}>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/courses" element={<Courses />} />
          <Route path="/courses/:courseId" element={<CourseDetail />} />
          <Route path="/checkout/:courseId" element={<Checkout />} />
          <Route path="/checkout/success" element={<StripeSuccess />} />
          <Route path="/order-confirmation/:orderId" element={<OrderConfirmation />} />
          <Route path="/orders/:orderId" element={<OrderPortal />} />
          <Route path="/orders/:orderId/bank-transfer" element={<BankTransferConfirmation />} />
          <Route path="/orders/:orderId/payment-proof" element={<ProofOfPaymentForm />} />
          <Route path="/orders/:orderId/attendees" element={<AttendeesForm />} />
          <Route path="/feedback/:orderId" element={<FeedbackPage />} />
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin" element={
            <ProtectedRoute>
              <AdminDashboard />
            </ProtectedRoute>
          } />
          <Route path="/about" element={<About />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/corporate" element={<CorporateTraining />} />
          <Route path="/services/consulting" element={<Consulting />} />
          <Route path="/services/in-house-training" element={<InHouseTraining />} />
          <Route path="/services/:serviceSlug" element={<ServiceDetail />} />
          <Route path="/consultants" element={<Consultants />} />
          <Route path="/verify/proforma/:number" element={<VerifyProforma />} />
          <Route path="/verify/receipt/:number" element={<VerifyProforma kind="receipt" />} />
          <Route path="/iso-training-consulting-usa" element={<UsLanding />} />
          {LEGACY_STATE_PAGES.map((k) => (
            <Route key={k} path={`/iso-training-consulting-${k}`} element={<Navigate to="/iso-training-consulting-usa" replace />} />
          ))}
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/tools" element={<Tools />} />
          <Route path="/tools/resources/:slug" element={<BlogDetail />} />
        </Routes>
      </main>
      {!isStandalone && <Footer />}
      {!isStandalone && <AIChatbot />}
      <Toaster position="top-right" />
    </>
  );
}

export default App;
